import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  BRIEF_AUDIENCES,
  type BriefAudience,
  type BriefFieldSummary,
} from '../../../shared/briefFields';
import type { TiptapEditor } from '../Editor/Editor';
import AudienceChip from './AudienceChip';
import PhoneIcon from './PhoneIcon';
import { extractInterviewQuestions } from '../../../shared/extractInterviewQuestions';
import { warmupTexts } from '../../../shared/warmupBank';
import { useBriefFields } from './useBriefFields';
import WarmupPicker from './WarmupPicker';

/**
 * The Fields drawer: every field of a brief in document order, on or off, and
 * who each one goes to. It is the outline and the control panel in one list,
 * which is why it lives a keystroke away from the writing rather than behind a
 * settings page.
 *
 * There is no state of its own to save. Every control dispatches an ordinary
 * editor command, the document changes, and the page's autosave carries it —
 * the same path a keystroke takes. The list re-reads itself off the editor on
 * every transaction, so it can never drift from the document.
 */
export default function FieldsPanel({
  editor,
  open,
  onClose,
  returnFocusTo,
}: {
  editor: TiptapEditor | null;
  open: boolean;
  onClose: () => void;
  returnFocusTo?: React.RefObject<HTMLElement | null>;
}) {
  // Read straight off the document, and again whenever the field set moves: a
  // change made from the ⋯ menu in the document, or an undo, has to show up
  // here without either side knowing about the other.
  const fields = useBriefFields(editor, open);
  // The outline hook only tracks field attributes. Bank edits change the body,
  // so counts and selected questions also subscribe to document updates.
  const [bodyRevision, setBodyRevision] = useState(0);
  useEffect(() => {
    if (!open || !editor) return;
    const sync = () => setBodyRevision(revision => revision + 1);
    editor.on('update', sync);
    return () => { editor.off('update', sync); };
  }, [editor, open]);
  const [title, setTitle] = useState('');
  const [audience, setAudience] = useState<BriefAudience>('client');
  const [armedId, setArmedId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<{ id: string; placement: 'before' | 'after' } | null>(null);
  // The drag handlers read these, not the state above: dragover and drop can
  // land in the same tick as dragstart, before React has committed anything.
  // The state is only there to draw the row's dimming and the drop marker.
  const dragIdRef = useRef<string | null>(null);
  const dropAtRef = useRef<{ id: string; placement: 'before' | 'after' } | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const headingId = useId();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      // A modal dialog owns the Tab order while it is open: without this,
      // Tab walks out through the scrim and into the editor behind it, where
      // nothing is visible and every control is unreachable by keyboard.
      if (e.key !== 'Tab') return;
      const stops = focusableWithin(panelRef.current);
      if (stops.length === 0) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      const outside = !panelRef.current?.contains(active);
      if (e.shiftKey && (outside || active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (outside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    return () => {
      returnFocusTo?.current?.focus();
    };
  }, [open, returnFocusTo]);

  const endDrag = useCallback(() => {
    dragIdRef.current = null;
    dropAtRef.current = null;
    setArmedId(null);
    setDragId(null);
    setDropAt(null);
  }, []);

  const questionCount = useMemo(
    () => open && editor ? extractInterviewQuestions(JSON.stringify(editor.getJSON()))?.length ?? 0 : 0,
    [editor, fields, open, bodyRevision],
  );
  const warmups = useMemo(
    () => open && editor ? warmupTexts(editor.getJSON().content?.find(node =>
      node.type === 'briefField' && node.attrs?.key === 'warmup')?.content ?? []) : [],
    [editor, fields, open, bodyRevision],
  );

  if (!open || !editor) return null;

  const on = fields.filter((f) => f.attrs.enabled);
  const off = fields.filter((f) => !f.attrs.enabled);
  const briefCount = on.filter((f) => f.attrs.shoot && f.attrs.key !== 'questions').length;
  const canAdd = title.trim().length > 0;

  function addField() {
    if (!editor || !canAdd) return;
    const added = editor.commands.addCustomBriefField({ title: title.trim(), audience });
    setTitle('');
    if (!added) return;
    // The new field is in the document, behind the drawer, with the caret
    // already in it. Close first, then take the focus back on the next tick:
    // the drawer returns focus to the Fields button as it unmounts, so
    // anything sooner would be undone.
    onClose();
    window.setTimeout(() => {
      if (!editor.isDestroyed) editor.commands.focus();
    }, 0);
  }

  function renderRow(field: BriefFieldSummary) {
    const { id, title: fieldTitle, audience: fieldAudience, enabled, shoot } = field.attrs;
    const questions = field.attrs.key === 'questions';
    const phoneOn = questions || shoot;
    const marker = dropAt?.id === id ? dropAt.placement : null;
    return (
      <li
        key={id}
        // Armed by the handle, not the row: a drag that can start anywhere
        // would fire on the switch and the chip too.
        draggable={armedId === id}
        onDragStart={(e) => {
          dragIdRef.current = id;
          setDragId(id);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', id);
        }}
        onDragEnd={endDrag}
        onDragOver={(e) => {
          const dragging = dragIdRef.current;
          if (!dragging || dragging === id) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          const box = e.currentTarget.getBoundingClientRect();
          const next = {
            id,
            placement: (e.clientY < box.top + box.height / 2 ? 'before' : 'after') as
              | 'before'
              | 'after',
          };
          const current = dropAtRef.current;
          if (current && current.id === next.id && current.placement === next.placement) return;
          dropAtRef.current = next;
          setDropAt(next);
        }}
        onDrop={(e) => {
          e.preventDefault();
          const dragging = dragIdRef.current;
          const target = dropAtRef.current;
          if (dragging && target && dragging !== target.id) {
            editor?.commands.moveBriefFieldTo(dragging, target.id, target.placement);
          }
          endDrag();
        }}
        className={`
          flex items-center gap-2.5 px-2 py-2 rounded-sm
          border-y-2 border-transparent transition-colors duration-150
          ${dragId === id ? 'opacity-40' : 'hover:bg-elevated'}
          ${marker === 'before' ? 'border-t-accent' : ''}
          ${marker === 'after' ? 'border-b-accent' : ''}
          ${enabled ? '' : 'opacity-60'}
        `}
      >
        <button
          type="button"
          aria-label="Drag to reorder"
          title="Drag to reorder, or Alt + arrow keys"
          onPointerDown={() => setArmedId(id)}
          onPointerUp={() => setArmedId(null)}
          onKeyDown={(e) => {
            if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
            e.preventDefault();
            editor?.commands.moveBriefField(id, e.key === 'ArrowUp' ? 'up' : 'down');
          }}
          className="
            shrink-0 cursor-grab select-none px-0.5 leading-none
            text-[13px] tracking-[-0.2em] text-faint
            hover:text-fg focus-visible:outline-none focus-visible:text-accent
          "
        >
          ⋮⋮
        </button>

        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={`${fieldTitle}: ${enabled ? 'in the document' : 'off, content kept'}`}
          title={
            enabled
              ? 'Switch off. The content is kept and the field drops into the hidden strip.'
              : 'Switch back on, in its original place.'
          }
          onClick={() =>
            enabled ? editor?.commands.hideBriefField(id) : editor?.commands.showBriefField(id)
          }
          className={`
            relative w-8 h-[18px] rounded-full shrink-0 cursor-pointer
            transition-colors duration-200
            ${enabled ? 'bg-accent' : 'bg-line'}
          `}
        >
          <span
            aria-hidden
            className={`
              absolute top-[2px] w-[14px] h-[14px] rounded-full bg-page
              transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]
              ${enabled ? 'left-[16px]' : 'left-[2px]'}
            `}
          />
        </button>

        <span className="flex-1 min-w-0 truncate text-[13px] text-fg" title={fieldTitle}>
          {fieldTitle}
        </span>

        <AudienceChip
          audience={fieldAudience}
          onClick={field.attrs.key === 'warmup' ? undefined : () =>
            editor?.commands.setBriefFieldAttrs(id, {
              audience: fieldAudience === 'client' ? 'internal' : 'client',
            })
          }
          title={field.attrs.key === 'warmup' ? 'Warm-ups are always internal.' : undefined}
        />
        <div className="w-7 flex justify-center shrink-0">
          {enabled && (
            <button
              type="button"
              role="switch"
              aria-checked={phoneOn}
              aria-label={`${fieldTitle}: ${phoneOn ? 'on the shoot view' : 'not on the shoot view'}`}
              aria-disabled={questions}
              title={questions
                ? 'Always in the shoot view'
                : shoot
                  ? "On the shoot view's Brief tab. Click to take it off."
                  : "Add to the shoot view's Brief tab."}
              onClick={() => {
                if (questions) return;
                editor?.commands.setBriefFieldAttrs(id, { shoot: !shoot });
              }}
              className={`
                w-6 h-6 flex items-center justify-center rounded-sm
                ${phoneOn
                  ? 'bg-accent/10 text-accent border border-accent/40'
                  : 'text-faint border border-line hover:text-fg hover:border-fg/30'}
                ${questions ? 'opacity-50 cursor-default' : 'cursor-pointer'}
              `}
            >
              <PhoneIcon />
            </button>
          )}
        </div>
      </li>
    );
  }

  return (
    <div className="fixed inset-0 z-40" data-print-hide>
      <div
        className="absolute inset-0 bg-ink-900/40 animate-fade-in"
        onClick={onClose}
        aria-hidden
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="
          absolute inset-y-0 right-0 w-full md:w-[380px]
          bg-card border-l border-line shadow-flare-lg
          flex flex-col animate-rise-subtle
        "
        style={{ animationDuration: '260ms' }}
      >
        <header className="shrink-0 flex items-start justify-between gap-3 px-5 py-4 border-b border-line">
          <div className="min-w-0">
            <span className="font-mono text-[11px] uppercase font-medium tracking-[0.12em] text-faint">
              // Fields
            </span>
            <h2
              id={headingId}
              className="font-display font-semibold text-[19px] tracking-[-0.01em] mt-1"
            >
              {fields.length} {fields.length === 1 ? 'field' : 'fields'}, {on.length} on
            </h2>
            <p className="text-[11px] text-faint leading-snug mt-1.5">
              Drag to reorder. The phone icon marks what goes on the shoot view's Brief tab.
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close the fields drawer"
            className="shrink-0 -mr-1 -mt-1 w-8 h-8 rounded-sm text-faint hover:text-fg hover:bg-elevated transition-colors duration-150 text-[17px] leading-none"
          >
            ×
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3">
          <GroupLabel trailing="Phone">In the document</GroupLabel>
          {on.length === 0 ? (
            <p className="px-2 py-2 text-[12px] text-faint">
              Every field is switched off. Turn one back on below.
            </p>
          ) : (
            <ul className="mb-1">{on.map(renderRow)}</ul>
          )}

          {off.length > 0 && (
            <>
              <GroupLabel>Off, content kept</GroupLabel>
              <ul>{off.map(renderRow)}</ul>
            </>
          )}
          <WarmupPicker texts={warmups} onChange={texts => { editor.commands.setBriefWarmups(texts); }} />
        </div>

        <div className="shrink-0 border-t border-line px-5 py-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-faint">
            // Shoot view
          </span>
          <p className="text-[12px] text-muted">
            {briefCount} {briefCount === 1 ? 'field' : 'fields'} on the Brief tab,{' '}
            {questionCount} {questionCount === 1 ? 'question' : 'questions'}. Off fields never show,
            whatever the phone icon says.
          </p>
        </div>

        <div className="shrink-0 border-t border-line bg-elevated px-5 py-4">
          <span className="block font-mono text-[11px] uppercase font-medium tracking-[0.12em] text-faint mb-2">
            // + Custom field
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canAdd) {
                e.preventDefault();
                addField();
              }
            }}
            placeholder="Give the field a title"
            aria-label="Custom field title"
            className="
              w-full px-3 py-2 rounded-sm bg-card border border-line
              text-[13px] text-fg placeholder:text-faint
              focus:outline-none focus:border-accent focus:shadow-focus
              transition-colors duration-150
            "
          />

          <span className="block font-mono text-[10px] uppercase tracking-[0.14em] text-faint mt-3 mb-1.5">
            Who sees it
          </span>
          <div role="radiogroup" aria-label="Who sees it" className="flex gap-2">
            {BRIEF_AUDIENCES.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={audience === value}
                onClick={() => setAudience(value)}
                className={`
                  flex-1 px-3 py-1.5 rounded-sm border text-[12px]
                  transition-colors duration-150
                  ${
                    audience === value
                      ? 'bg-accent/10 border-accent text-accent font-medium'
                      : 'bg-card border-line text-muted hover:border-fg/30'
                  }
                `}
              >
                {value === 'internal' ? 'Internal' : 'Client'}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={addField}
            disabled={!canAdd}
            className="
              w-full mt-3 px-3 py-2 rounded-sm
              bg-accent text-accent-fg font-medium text-[13px]
              hover:bg-accent-hover transition-colors duration-150
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-accent
            "
          >
            Add field
          </button>
        </div>
      </aside>
    </div>
  );
}

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** The drawer's own tab stops, in document order, skipping anything inert. */
function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
  );
}

function GroupLabel({ children, trailing }: { children: React.ReactNode; trailing?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 px-2 pt-2 pb-1.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint whitespace-nowrap">
        {children}
      </span>
      <span className="flex-1 h-px bg-line-soft" aria-hidden />
      {trailing && (
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint w-7 text-center shrink-0">
          {trailing}
        </span>
      )}
    </div>
  );
}
